/**
 * Domain Layer - Pagination Hook
 * Handles paginated data fetching with infinite scroll support
 */

import { useCallback, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { PAGINATION, TIMINGS } from "@/constants";
import type {
  PaginationParams,
  UsePaginationOptions,
  UsePaginationResult,
} from "@/shared/core/types";

/**
 * Hook for paginated data fetching with infinite scroll
 */
export function usePagination<T>({
  queryKey,
  defaultParams = {
    page: PAGINATION.DEFAULT_PAGE,
    perPage: PAGINATION.DEFAULT_PER_PAGE,
  },
  enabled = true,
  fetchData,
}: Omit<UsePaginationOptions<T>, "queryFn"> & {
  fetchData: (params: PaginationParams) => Promise<{
    response: T[];
    meta: {
      currentPage: number;
      lastPage: number;
      [key: string]: unknown;
    };
  }>;
}): UsePaginationResult<T> {
  const [params, setParams] = useState<PaginationParams>(defaultParams);

  // Create the query function that accepts a page parameter
  const queryFunction = useCallback(
    async ({ pageParam = PAGINATION.DEFAULT_PAGE }) => {
      const queryParams = { ...params, page: pageParam };
      return await fetchData(queryParams);
    },
    [params, fetchData]
  );

  // Set up the infinite query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: [...queryKey, params],
    queryFn: queryFunction,
    initialPageParam: PAGINATION.DEFAULT_PAGE,
    getNextPageParam: (lastPage) => {
      const { currentPage, lastPage: maxPage } = lastPage.meta;
      return currentPage < maxPage ? currentPage + 1 : undefined;
    },
    enabled,
    staleTime: TIMINGS.PAGINATION_STALE_TIME,
    gcTime: TIMINGS.PAGINATION_GC_TIME,
    refetchOnWindowFocus: false,
  });

  // Flatten the pages into a single array of items
  const flattenedData = data ? data.pages.flatMap((page) => page.response) : [];

  return {
    data: flattenedData,
    isLoading,
    isFetching,
    isError,
    error,
    hasNextPage: hasNextPage || false,
    fetchNextPage,
    refetch,
    params,
    setParams: (newParams) => {
      setParams(newParams);
      refetch();
    },
    isEmpty: flattenedData.length === 0 && !isLoading,
  };
}
